/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import type { ShopperSearch } from '@/scapi';
import { Checkbox } from '@/components/ui/checkbox';
import type { RefinementProps } from '../types';
import { mapColorNameToHex } from '../refine-color';

export default function DefaultRefinement({
    values,
    attributeId,
    isFilterSelected,
    toggleFilter,
}: RefinementProps): ReactElement {
    const isColorRefinement = attributeId.toLowerCase().includes('color');
    const isSizeRefinement = attributeId.toLowerCase().includes('size');

    const getCheckmarkColor = (color: string) => {
        const lightColors = ['#ffffff', '#f5f5dc', '#ffd700', '#c0c0c0', '#e5e7eb', 'white', 'beige', 'off-white'];
        const normalized = color.toLowerCase().trim();
        if (lightColors.includes(normalized) || normalized.includes('white') || normalized.includes('beige')) {
            return '#000000';
        }
        return '#ffffff';
    };

    const mapSizeCodeToLabel = (sizeCode: string): string => {
        const sizeMap: Record<string, string> = {
            s: 'Small',
            m: 'Medium',
            l: 'Large',
            xl: 'Extra Large',
            xxl: 'Double Extra Large',
            xs: 'Extra Small',
        };
        const normalized = sizeCode.toLowerCase().trim();
        return sizeMap[normalized] || sizeCode;
    };

    return (
        <div className="space-y-0">
            {values.map((value: ShopperSearch.schemas['ProductSearchRefinementValue'], idx) => {
                const id = `refine-${attributeId}-${idx}`;
                const isSelected = isFilterSelected(attributeId, value.value);
                const colorHex = isColorRefinement
                    ? mapColorNameToHex(value.value) || mapColorNameToHex(value.label || '') || value.value
                    : null;

                const checkboxStyle = isColorRefinement && colorHex
                    ? ({
                          '--checkbox-color': colorHex,
                          backgroundColor: colorHex,
                          borderColor: colorHex,
                          color: getCheckmarkColor(colorHex),
                      } as React.CSSProperties)
                    : undefined;

                const displayLabel = isSizeRefinement
                    ? mapSizeCodeToLabel(value.label || value.value)
                    : value.label || value.value;

                return (
                    <label
                        key={`${attributeId}:${value.value}`}
                        htmlFor={id}
                        className="flex items-center py-2 rounded-none hover:bg-muted/30 cursor-pointer">
                        <Checkbox
                            id={id}
                            checked={isSelected}
                            onCheckedChange={() => toggleFilter(attributeId, value.value)}
                            className={isColorRefinement ? 'color-checkbox' : 'size-4'}
                            style={checkboxStyle}
                        />
                        <span className="ml-3 text-sm font-medium">{displayLabel}</span>
                        {/* {value.hitCount !== undefined && (
                            <span className="ml-auto text-xs bg-muted/50 px-2 py-1 rounded-full">{value.hitCount}</span>
                        )} */}
                    </label>
                );
            })}
        </div>
    );
}
